import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Image, Input, Select, Space, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import request from '../../../api/request';

export const CUSTOMER_SERVICE_CATEGORY = 'customer_service';

const DEFAULT_CUSTOMER_SERVICE_DESCRIPTION = '有办卡、订单进度或售后问题，请添加客服咨询。';

export interface CustomerServiceArticle {
  id: number;
  title: string;
  content: string;
  author: string;
  category: string;
  status: string;
  coverImage?: string | null;
}

interface CustomerServiceConfig {
  title?: string;
  description?: string;
  wechatName?: string;
  contactValue?: string;
  serviceTime?: string;
  qrCodeUrl?: string;
  acquisitionLink?: string;
}

interface CustomerServiceFormValues {
  title: string;
  description: string;
  wechatName?: string;
  contactValue?: string;
  serviceTime?: string;
  qrCodeUrl?: string;
  acquisitionLink?: string;
  status: string;
}

interface CustomerServiceConfigPanelProps {
  article: CustomerServiceArticle | null;
  onSaved: () => Promise<void> | void;
}

const safeParseCustomerServiceConfig = (content?: string): CustomerServiceConfig | null => {
  const value = content?.trim();
  if (!value || !value.startsWith('{')) return null;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const getCustomerServiceFormValues = (article?: CustomerServiceArticle | null): CustomerServiceFormValues => {
  const parsed = safeParseCustomerServiceConfig(article?.content);
  return {
    title: parsed?.title || article?.title || '联系客服',
    description: parsed?.description || (parsed ? '' : article?.content) || DEFAULT_CUSTOMER_SERVICE_DESCRIPTION,
    wechatName: parsed?.wechatName || '',
    contactValue: parsed?.contactValue || '',
    serviceTime: parsed?.serviceTime || '',
    qrCodeUrl: parsed?.qrCodeUrl || article?.coverImage || '',
    acquisitionLink: parsed?.acquisitionLink || '',
    status: article?.status || 'published',
  };
};

const toCustomerServiceContent = (values: CustomerServiceFormValues) =>
  JSON.stringify(
    {
      title: values.title.trim(),
      description: values.description.trim(),
      wechatName: values.wechatName?.trim() || '',
      contactValue: values.contactValue?.trim() || '',
      serviceTime: values.serviceTime?.trim() || '',
      qrCodeUrl: values.qrCodeUrl?.trim() || '',
      acquisitionLink: values.acquisitionLink?.trim() || '',
    },
    null,
    2,
  );

const CustomerServiceConfigPanel: React.FC<CustomerServiceConfigPanelProps> = ({ article, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [uploadingQrCode, setUploadingQrCode] = useState(false);
  const [form] = Form.useForm<CustomerServiceFormValues>();
  const qrCodeUrl = Form.useWatch('qrCodeUrl', form);

  useEffect(() => {
    form.setFieldsValue(getCustomerServiceFormValues(article));
  }, [article, form]);

  const saveCustomerService = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        title: values.title.trim(),
        content: toCustomerServiceContent(values),
        author: article?.author || '管理员',
        category: CUSTOMER_SERVICE_CATEGORY,
        status: values.status,
        coverImage: values.qrCodeUrl?.trim() || undefined,
      };

      setSaving(true);
      if (article) {
        await request({ url: `/api/content/articles/${article.id}`, method: 'PATCH', data: payload });
        message.success('客服配置已更新');
      } else {
        await request({ url: '/api/content/articles', method: 'POST', data: payload });
        message.success('客服配置已创建');
      }
      await onSaved();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '保存客服配置失败');
    } finally {
      setSaving(false);
    }
  };

  const uploadCustomerQrCode = async (file: File) => {
    const extOk = /\.(jpe?g|png|webp)$/i.test(file.name);
    const mimeOk = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!extOk || !mimeOk || !file.size || file.size > 5 * 1024 * 1024) {
      message.error('请上传 5MB 内的 jpg/jpeg/png/webp 图片');
      return Upload.LIST_IGNORE;
    }

    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingQrCode(true);
      const data = await request.post<any, { url: string }>('/api/content/assets/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      form.setFieldValue('qrCodeUrl', data.url);
      message.success('客服二维码已上传');
    } catch (error: any) {
      message.error(error?.response?.data?.message || '上传客服二维码失败');
    } finally {
      setUploadingQrCode(false);
    }
    return Upload.LIST_IGNORE;
  };

  return (
    <Card bordered={false} className="customer-service-card">
      <Alert
        type="info"
        showIcon
        className="customer-service-alert"
        message="企业微信客服入口"
        description="第一版使用免费二维码方案；如果后续开通官方获客链接，填入链接后客户端会优先展示“立即添加客服”按钮。"
      />
      <Form form={form} layout="vertical" className="customer-service-form">
        <Form.Item name="title" label="页面标题" rules={[{ required: true, message: '请输入页面标题' }]}>
          <Input placeholder="联系客服" />
        </Form.Item>
        <Form.Item name="description" label="客服说明" rules={[{ required: true, message: '请输入客服说明' }]}>
          <Input.TextArea rows={4} placeholder={DEFAULT_CUSTOMER_SERVICE_DESCRIPTION} />
        </Form.Item>
        <Space size={16} align="start" className="customer-service-row">
          <Form.Item name="wechatName" label="企业微信名称">
            <Input placeholder="例如：青楚客服" />
          </Form.Item>
          <Form.Item name="contactValue" label="微信号 / 手机号">
            <Input placeholder="用于复制兜底" />
          </Form.Item>
          <Form.Item name="serviceTime" label="服务时间">
            <Input placeholder="例如：09:00-22:00" />
          </Form.Item>
        </Space>
        <Form.Item label="客服二维码 URL">
          <Space.Compact className="customer-service-url-input">
            <Form.Item name="qrCodeUrl" noStyle>
              <Input placeholder="上传后自动填入，或手动填写图片 URL" />
            </Form.Item>
            <Upload accept="image/jpeg,image/png,image/webp" showUploadList={false} beforeUpload={uploadCustomerQrCode}>
              <Button icon={<UploadOutlined />} loading={uploadingQrCode}>
                上传二维码
              </Button>
            </Upload>
          </Space.Compact>
          {qrCodeUrl && <Image src={qrCodeUrl} width={180} className="customer-service-qr-preview" />}
        </Form.Item>
        <Form.Item name="acquisitionLink" label="官方获客链接（可选）">
          <Input placeholder="开通企业微信官方获客链接后填写，例如：https://..." />
        </Form.Item>
        <Form.Item name="status" label="发布状态" rules={[{ required: true, message: '请选择状态' }]}>
          <Select
            options={[
              { label: '已发布', value: 'published' },
              { label: '草稿', value: 'draft' },
              { label: '归档', value: 'archived' },
            ]}
          />
        </Form.Item>
        <Space>
          <Button type="primary" loading={saving} onClick={saveCustomerService}>
            保存客服配置
          </Button>
          <Button onClick={() => form.setFieldsValue(getCustomerServiceFormValues(article))}>恢复当前配置</Button>
        </Space>
      </Form>
    </Card>
  );
};

export default CustomerServiceConfigPanel;
